import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Calculator, AlertCircle, MapPin, FileText, CheckCircle2, DollarSign, Scale, Lightbulb, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const US_STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "Washington D.C."
};

const TaxTab = ({ businessId, token }) => {
  const [taxData, setTaxData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [state, setState] = useState("CA");
  const [filing, setFiling] = useState("single");
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("estimates"); // estimates, compare, deductions, forms
  const [comparison, setComparison] = useState(null);
  const [deductions, setDeductions] = useState(null);
  const [w2Data, setW2Data] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [summary1099, setSummary1099] = useState(null);
  const [showAddContractor, setShowAddContractor] = useState(false);
  const [showPayContractor, setShowPayContractor] = useState(null);
  const [contractorForm, setContractorForm] = useState({ name: '', email: '' });
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: '', description: '' });
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentQuarter, setPaymentQuarter] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const storedToken = token || sessionStorage.getItem('mybookie_token');
  const authHeaders = storedToken ? { headers: { Authorization: `Bearer ${storedToken}` } } : {};

  const fetchTaxEstimates = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/mybookie/tax-estimates/${businessId}?year=${year}&state=${state}&filing_status=${filing}`,
        authHeaders
      );
      setTaxData(res.data);
    } catch (err) {
      console.error("Failed to load tax estimates:", err);
      toast({ title: "Error", description: "Failed to load tax estimates", variant: "destructive" });
    } finally { setLoading(false); }
  }, [businessId, year, state, filing, storedToken]);

  const fetchComparison = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/tax/compare-filing/${businessId}?year=${year}&state=${state}`, authHeaders);
      setComparison(res.data);
    } catch (err) { console.error("Failed to load filing comparison:", err); }
  }, [businessId, year, state, storedToken]);

  const fetchDeductions = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/tax/deduction-suggestions/${businessId}?year=${year}`, authHeaders);
      setDeductions(res.data);
    } catch (err) { console.error("Failed to load deductions:", err); }
  }, [businessId, year, storedToken]);

  useEffect(() => { fetchTaxEstimates(); }, [fetchTaxEstimates]);
  useEffect(() => { if (activeView === 'compare') fetchComparison(); }, [activeView, fetchComparison]);
  useEffect(() => { if (activeView === 'deductions') fetchDeductions(); }, [activeView, fetchDeductions]);

  const fetchW2Data = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await axios.get(`${API}/api/mybookie/tax/w2-data/${businessId}?year=${year}`, authHeaders);
      setW2Data(res.data);
    } catch (err) { console.error("Failed to load W-2 data:", err); }
  }, [businessId, year, storedToken]);

  const fetchContractors = useCallback(async () => {
    if (!businessId) return;
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/contractors/${businessId}`, authHeaders),
        axios.get(`${API}/api/mybookie/tax/1099-summary/${businessId}?year=${year}`, authHeaders),
      ]);
      setContractors(cRes.data);
      setSummary1099(sRes.data);
    } catch (err) { console.error("Failed to load contractors:", err); }
  }, [businessId, year, storedToken]);

  useEffect(() => {
    if (activeView === 'forms') { fetchW2Data(); fetchContractors(); }
  }, [activeView, fetchW2Data, fetchContractors]);

  const handleAddContractor = async () => {
    if (!contractorForm.name) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/mybookie/contractors`, { business_id: businessId, ...contractorForm }, authHeaders);
      toast({ title: "Contractor added" });
      setShowAddContractor(false);
      setContractorForm({ name: '', email: '' });
      fetchContractors();
    } catch (err) { console.error("Add contractor failed:", err); toast({ title: "Error", description: "Failed to add contractor", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handlePayContractor = async () => {
    if (!paymentForm.amount || !showPayContractor) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/mybookie/contractor-payments`, {
        business_id: businessId, contractor_id: showPayContractor,
        amount: parseFloat(paymentForm.amount), date: paymentForm.date, description: paymentForm.description,
      }, authHeaders);
      toast({ title: "Payment recorded" });
      setShowPayContractor(null);
      setPaymentForm({ amount: '', date: '', description: '' });
      fetchContractors();
    } catch (err) { console.error("Contractor payment failed:", err); toast({ title: "Error", description: "Failed to record payment", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleRecordPayment = async () => {
    if (!paymentQuarter || !paymentAmount) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/mybookie/tax-payments`, {
        business_id: businessId, quarter: paymentQuarter,
        year: parseInt(year), amount: parseFloat(paymentAmount),
        payment_date: paymentDate || new Date().toISOString().slice(0, 10),
      }, authHeaders);
      toast({ title: "Payment recorded", description: `${paymentQuarter} ${year}: $${parseFloat(paymentAmount).toFixed(2)}` });
      setShowPaymentDialog(false);
      setPaymentAmount('');
      fetchTaxEstimates();
    } catch (err) {
      console.error("Tax payment failed:", err);
      toast({ title: "Error", description: "Failed to record payment", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

  if (!taxData && !loading) {
    return (
      <div className="text-center py-12">
        <Calculator className="w-10 h-10 text-[#8A948F] mx-auto mb-3" />
        <p className="text-[#5C6661]">{businessId ? 'No tax data available yet. Add expenses and income to see tax estimates.' : 'Select a business to view tax estimates'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tax-tab">
      {/* Header with filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#111614]">Tax Center</h2>
          <p className="text-sm text-[#5C6661]">Progressive federal + state brackets with standard deduction</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[100px]" data-testid="tax-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="w-[160px]" data-testid="tax-state-select">
              <MapPin className="w-3.5 h-3.5 mr-1 text-[#5C6661]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(US_STATES).map(([code, name]) => (
                <SelectItem key={code} value={code}>{code} - {name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filing} onValueChange={setFiling}>
            <SelectTrigger className="w-[130px]" data-testid="tax-filing-select">
              <FileText className="w-3.5 h-3.5 mr-1 text-[#5C6661]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="married">Married</SelectItem>
              <SelectItem value="head_of_household">Head of HH</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-1 bg-[#F6F5F2] p-1 rounded-sm">
        {[
          { id: 'estimates', label: 'Quarterly Estimates', icon: Calculator },
          { id: 'compare', label: 'Filing Comparison', icon: Scale },
          { id: 'deductions', label: 'Deduction Optimizer', icon: Lightbulb },
          { id: 'forms', label: 'Tax Forms', icon: FileText },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveView(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-sm rounded-sm transition-colors ${
              activeView === id ? 'bg-white text-[#0A2B1D] font-medium shadow-sm' : 'text-[#5C6661] hover:text-[#111614]'
            }`} data-testid={`tax-view-${id}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* ===== ESTIMATES VIEW ===== */}
      {activeView === 'estimates' && taxData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">YTD Income</p>
                <p className="text-lg font-semibold text-green-600">{fmt(taxData.ytd_income)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">YTD Expenses</p>
                <p className="text-lg font-semibold text-red-600">{fmt(taxData.ytd_expenses)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Net Income</p>
                <p className="text-lg font-semibold text-[#0A2B1D]">{fmt(taxData.ytd_net)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Std. Deduction</p>
                <p className="text-lg font-semibold text-blue-600">{fmt(taxData.standard_deduction)}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#FFF8E1] border-[#FFE082]">
              <CardContent className="p-4">
                <p className="text-xs text-[#5D4037]">Total Tax Est.</p>
                <p className="text-lg font-bold text-[#E65100]" data-testid="total-tax-estimate">{fmt(taxData.total_annual_estimate)}</p>
              </CardContent>
            </Card>
            <Card className={taxData.total_remaining > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Remaining Due</p>
                <p className={`text-lg font-bold ${taxData.total_remaining > 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="tax-remaining">
                  {fmt(taxData.total_remaining)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quarterly Breakdown with Payment Tracking */}
          {taxData.quarters && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Quarterly Breakdown ({taxData.filing_status === 'married' ? 'Married Filing Jointly' : taxData.filing_status === 'head_of_household' ? 'Head of Household' : 'Single'})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-[#F6F5F2]">
                        <th className="text-left py-2.5 px-3 font-medium text-xs">Quarter</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs">Income</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs">Expenses</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs">SE Tax</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs">Fed Tax</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs">State Tax</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs text-[#E65100]">Total Due</th>
                        <th className="text-right py-2.5 px-3 font-medium text-xs text-green-600">Paid</th>
                        <th className="text-left py-2.5 px-3 font-medium text-xs">Due Date</th>
                        <th className="text-center py-2.5 px-3 font-medium text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxData.quarters.map((q) => (
                        <tr key={q.quarter} className="border-b hover:bg-gray-50">
                          <td className="py-2.5 px-3 font-medium">{q.quarter}</td>
                          <td className="py-2.5 px-3 text-right text-green-600">{fmt(q.income)}</td>
                          <td className="py-2.5 px-3 text-right text-red-600">{fmt(q.expenses)}</td>
                          <td className="py-2.5 px-3 text-right">{fmt(q.self_employment_tax)}</td>
                          <td className="py-2.5 px-3 text-right">{fmt(q.federal_income_tax)}</td>
                          <td className="py-2.5 px-3 text-right text-blue-600">{fmt(q.state_income_tax)}</td>
                          <td className="py-2.5 px-3 text-right font-semibold text-[#E65100]">{fmt(q.total_estimated_tax)}</td>
                          <td className="py-2.5 px-3 text-right text-green-600">{q.amount_paid > 0 ? fmt(q.amount_paid) : '—'}</td>
                          <td className="py-2.5 px-3 text-[#5C6661]">{q.due_date}</td>
                          <td className="py-2.5 px-3 text-center">
                            {q.is_paid ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> Paid</span>
                            ) : (
                              <Button variant="ghost" size="sm" className="h-6 text-xs text-[#0A2B1D] hover:bg-[#E2ECE7]"
                                onClick={() => { setPaymentQuarter(q.quarter); setPaymentAmount(String(q.total_estimated_tax)); setPaymentDate(''); setShowPaymentDialog(true); }}
                                data-testid={`record-payment-${q.quarter}`}>
                                <DollarSign className="w-3 h-3 mr-0.5" /> Record
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Federal Brackets Reference */}
          {taxData.federal_brackets && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  2026 Federal Tax Brackets ({taxData.filing_status === 'married' ? 'MFJ' : 'Single'}) — after ${(taxData.standard_deduction || 0).toLocaleString()} standard deduction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                  {taxData.federal_brackets.map((b, i) => (
                    <div key={b.rate} className="bg-[#F6F5F2] rounded p-2.5 text-center">
                      <p className="text-sm font-semibold text-[#0A2B1D]">{(b.rate * 100).toFixed(0)}%</p>
                      <p className="text-xs text-[#5C6661]">
                        {b.limit ? `up to ${fmt(b.limit)}` : `over ${fmt(taxData.federal_brackets[i - 1]?.limit || 0)}`}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* State Progressive Brackets */}
          {taxData.state_has_progressive && taxData.state_brackets && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {state} Progressive State Tax Brackets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {taxData.state_brackets.map((b, i) => (
                    <div key={`${b.rate}-${b.limit || 'top'}`} className="bg-blue-50 border border-blue-100 rounded p-2.5 text-center">
                      <p className="text-sm font-semibold text-blue-700">{(b.rate * 100).toFixed(2)}%</p>
                      <p className="text-xs text-[#5C6661]">
                        {b.limit ? `up to ${fmt(b.limit)}` : `over ${fmt(taxData.state_brackets[i - 1]?.limit || 0)}`}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== FILING COMPARISON VIEW ===== */}
      {activeView === 'compare' && comparison && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Filing Status Comparison — {comparison.year} (Net Income: {fmt(comparison.net_income)})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {comparison.comparisons.map((c) => {
                const isMin = c.total_tax === Math.min(...comparison.comparisons.map(x => x.total_tax));
                return (
                  <div key={c.filing_status} className={`p-4 rounded border ${isMin ? 'border-green-300 bg-green-50' : 'border-[#E6E8E6]'}`}
                    data-testid={`compare-${c.filing_status}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-sm text-[#111614]">{c.label}</h3>
                      {isMin && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Lowest</span>}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-[#5C6661]">Std Deduction</span><span>{fmt(c.standard_deduction)}</span></div>
                      <div className="flex justify-between"><span className="text-[#5C6661]">Taxable Income</span><span>{fmt(c.taxable_income)}</span></div>
                      <hr className="my-1" />
                      <div className="flex justify-between"><span className="text-[#5C6661]">Federal Tax</span><span>{fmt(c.federal_tax)}</span></div>
                      <div className="flex justify-between"><span className="text-[#5C6661]">State Tax ({state})</span><span className="text-blue-600">{fmt(c.state_tax)}</span></div>
                      <div className="flex justify-between"><span className="text-[#5C6661]">SE Tax</span><span>{fmt(c.se_tax)}</span></div>
                      <hr className="my-1" />
                      <div className="flex justify-between font-semibold">
                        <span>Total Tax</span>
                        <span className={isMin ? 'text-green-600' : 'text-[#E65100]'}>{fmt(c.total_tax)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#8A948F]">
                        <span>Effective Rate</span><span>{c.effective_rate}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== DEDUCTIONS VIEW ===== */}
      {activeView === 'deductions' && deductions && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Total Expenses</p>
                <p className="text-lg font-semibold">{fmt(deductions.total_expenses)}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <p className="text-xs text-green-700">Total Deductible</p>
                <p className="text-lg font-semibold text-green-600" data-testid="total-deductible">{fmt(deductions.total_deductible)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-[#5C6661]">Mileage Deduction</p>
                <p className="text-lg font-semibold">{fmt(deductions.mileage_deduction?.deduction)}</p>
                <p className="text-[10px] text-[#8A948F]">{deductions.mileage_deduction?.total_miles?.toLocaleString()} mi @ $0.67</p>
              </CardContent>
            </Card>
            <Card className="bg-[#E2ECE7] border-[#B4C9BE]">
              <CardContent className="p-4">
                <p className="text-xs text-[#0A2B1D]">Est. Tax Savings</p>
                <p className="text-lg font-bold text-[#0A2B1D]">{fmt(deductions.estimated_tax_savings)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Expense Deductions */}
          {deductions.expense_deductions?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Schedule C Deductions by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-[#F6F5F2]">
                      <th className="text-left py-2 px-3 text-xs">Category</th>
                      <th className="text-left py-2 px-3 text-xs">Schedule C Line</th>
                      <th className="text-right py-2 px-3 text-xs">Total Spent</th>
                      <th className="text-right py-2 px-3 text-xs">Deductible</th>
                      <th className="text-right py-2 px-3 text-xs">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.expense_deductions.map((d) => (
                      <tr key={d.category} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{d.category}</td>
                        <td className="py-2 px-3 text-[#5C6661] text-xs">{d.schedule_c_line} — {d.description}</td>
                        <td className="py-2 px-3 text-right">{fmt(d.amount)}</td>
                        <td className="py-2 px-3 text-right text-green-600 font-medium">{fmt(d.deductible_amount)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{(d.deduction_rate * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Missing Deductions */}
          {deductions.missing_deductions?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                  <Lightbulb className="w-4 h-4" /> You May Be Missing These Deductions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {deductions.missing_deductions.map((m) => (
                    <div key={m.category} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm text-[#111614]">{m.category}</p>
                        <p className="text-xs text-[#5C6661]">{m.tip}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ===== TAX FORMS VIEW ===== */}
      {activeView === 'forms' && (
        <>
          {/* W-2 Section */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" /> W-2 Wage & Tax Statements — {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {w2Data?.employees?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-[#F6F5F2]">
                      <th className="text-left py-2 px-3 text-xs">Employee</th>
                      <th className="text-left py-2 px-3 text-xs">State</th>
                      <th className="text-right py-2 px-3 text-xs">Gross Wages</th>
                      <th className="text-right py-2 px-3 text-xs">Fed Tax</th>
                      <th className="text-right py-2 px-3 text-xs">State Tax</th>
                      <th className="text-right py-2 px-3 text-xs">FICA</th>
                      <th className="text-right py-2 px-3 text-xs">Net Pay</th>
                      <th className="text-center py-2 px-3 text-xs">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w2Data.employees.map(emp => (
                      <tr key={emp.employee_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{emp.name}</td>
                        <td className="py-2 px-3 text-[#5C6661]">{emp.state}</td>
                        <td className="py-2 px-3 text-right">{fmt(emp.gross)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{fmt(emp.federal_tax)}</td>
                        <td className="py-2 px-3 text-right text-blue-600">{fmt(emp.state_tax)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{fmt(emp.fica)}</td>
                        <td className="py-2 px-3 text-right font-medium text-green-600">{fmt(emp.net)}</td>
                        <td className="py-2 px-3 text-center">
                          <Button variant="ghost" size="sm" className="h-7 text-xs"
                            data-testid={`download-w2-${emp.employee_id}`}
                            onClick={() => window.open(`${API}/api/mybookie/tax/w2/${businessId}/${emp.employee_id}?year=${year}`, '_blank')}>
                            W-2 PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-[#5C6661] text-center py-6">No payroll data for {year}. Run payroll to generate W-2 forms.</p>
              )}
            </CardContent>
          </Card>

          {/* 1099-NEC Section */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4" /> 1099-NEC Contractor Payments — {year}
                </CardTitle>
                <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setShowAddContractor(true)} data-testid="add-contractor-btn">
                  + Add Contractor
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {summary1099?.contractors?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-[#F6F5F2]">
                      <th className="text-left py-2 px-3 text-xs">Contractor</th>
                      <th className="text-left py-2 px-3 text-xs">Email</th>
                      <th className="text-right py-2 px-3 text-xs">Total Paid</th>
                      <th className="text-right py-2 px-3 text-xs">Payments</th>
                      <th className="text-center py-2 px-3 text-xs">1099 Required</th>
                      <th className="text-center py-2 px-3 text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary1099.contractors.map(c => (
                      <tr key={c.contractor_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{c.name}</td>
                        <td className="py-2 px-3 text-[#5C6661]">{c.email || '—'}</td>
                        <td className="py-2 px-3 text-right">{fmt(c.total_paid)}</td>
                        <td className="py-2 px-3 text-right text-[#5C6661]">{c.payment_count}</td>
                        <td className="py-2 px-3 text-center">
                          {c.requires_1099 ? (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Required</span>
                          ) : (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Under $600</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-[#0A2B1D]"
                            onClick={() => { setShowPayContractor(c.contractor_id); setPaymentForm({ amount: '', date: '', description: '' }); }}
                            data-testid={`pay-contractor-${c.contractor_id}`}>
                            <DollarSign className="w-3 h-3 mr-0.5" /> Pay
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 text-xs"
                            data-testid={`download-1099-${c.contractor_id}`}
                            onClick={() => window.open(`${API}/api/mybookie/tax/1099/${businessId}/${c.contractor_id}?year=${year}`, '_blank')}>
                            1099 PDF
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-[#5C6661] text-center py-6">No contractors yet. Add contractors and record payments to generate 1099-NEC forms.</p>
              )}
              {summary1099 && summary1099.requiring_1099 > 0 && (
                <p className="text-xs text-amber-600 mt-3 font-medium">
                  {summary1099.requiring_1099} contractor(s) require a 1099-NEC filing (paid $600+). File by January 31, {parseInt(year) + 1}.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Add Contractor Dialog */}
          <Dialog open={showAddContractor} onOpenChange={setShowAddContractor}>
            <DialogContent data-testid="add-contractor-dialog">
              <DialogHeader><DialogTitle>Add Contractor</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={contractorForm.name} onChange={e => setContractorForm({ ...contractorForm, name: e.target.value })} data-testid="contractor-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={contractorForm.email} onChange={e => setContractorForm({ ...contractorForm, email: e.target.value })} data-testid="contractor-email" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddContractor(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={handleAddContractor} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={saving} data-testid="save-contractor-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Contractor'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Record Contractor Payment Dialog */}
          <Dialog open={!!showPayContractor} onOpenChange={(v) => { if (!v) setShowPayContractor(null); }}>
            <DialogContent data-testid="pay-contractor-dialog">
              <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Amount ($)</Label>
                  <Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="contractor-payment-amount" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={paymentForm.date} onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} data-testid="contractor-payment-date" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={paymentForm.description} onChange={e => setPaymentForm({ ...paymentForm, description: e.target.value })} data-testid="contractor-payment-desc" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPayContractor(null)} className="rounded-sm">Cancel</Button>
                <Button onClick={handlePayContractor} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={saving} data-testid="save-contractor-payment-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Disclaimer */}
      <Card className="bg-[#E2ECE7] border-[#B4C9BE]">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#0A2B1D] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#111614]">
            <p className="font-medium mb-1">Disclaimer</p>
            <p className="text-[#5C6661]">
              Estimates use 2026 progressive federal brackets, progressive state brackets for major states (CA, NY, NJ, IL, etc.),
              standard deductions, and SE tax (15.3% on 92.35% of net). Actual tax liability may vary. Consult a CPA for accurate filings.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle>Record Tax Payment — {paymentQuarter} {year}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount Paid ($)</Label>
              <Input type="number" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} data-testid="payment-amount" />
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} data-testid="payment-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleRecordPayment} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" disabled={saving} data-testid="save-payment-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxTab;
